<template>
  <div class="cash_drop__report">
    <ReportFormat @show_result="show_result" :is_table_show="false" :formSubmitBtnText="'Download'" :isLoading="loading">
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="date" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" name="Report From Date" />
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Register</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  class="w-full"
                  :value="registerId"
                  @input="(item) => (registerId = item && item.value)"
                  name="Register"
                  placeholder="Select Register"
                  label="register_name"
                  val="id"
                  action="register/getRegisterList"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Register')">{{ errors.first('Register') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>User</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  class="w-full"
                  :value="prepareUserId"
                  @input="(item) => (prepareUserId = item && item.value)"
                  name="User"
                  placeholder="Select User"
                  :options="prepareUserOptions"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('User')">{{ errors.first('User') }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ReportFormat>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import moment from 'moment'
import { dateMixins } from '@/mixins/dateMixins.js'
import { dollarTypes } from '@/utils/enums'
import { mapMutations } from 'vuex'

export default {
  name: 'CashDropReport',

  components: {
    ReportFormat,
    DatePicker,
    Select2
  },

  data() {
    return {
      date: moment().format('MM-DD-YYYY'),

      prepareUserOptions: [],
      registerId: null,
      prepareUserId: null,
      dollarTypes,
      loading: false
    }
  },

  mixins: [dateMixins],

  async mounted() {
    // get prepare user list
    await this.getPrepareUserList()
  },

  methods: {
    ...mapMutations('register', { set_registers_state: 'SET_STATE' }),

    async getPrepareUserList() {
      this.loading = true
      this.$store
        .dispatch('register/getPrepareUserList', { page: 1, limit: 0, type: 'cash_drop' })
        .then((res) => {
          this.loading = false
          this.prepareUserOptions = res.data.reduce((acc, item) => {
            item.label = item.username
            item.value = item.id
            acc.push(item)

            return acc
          }, [])
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    async show_result() {
      if (!(await this.$validator.validate())) {
        return false
      }
      this.loading = true
      this.$store
        .dispatch('register/getCashFloatList', { page: 1, limit: 0, date: moment(this.date).format('YYYY-MM-DD'), perpareUserId: this.prepareUserId, registerId: this.registerId })
        .then((res) => {
          this.exportCashFloatData(res.data)
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    async exportCashFloatData(data) {
      const wb = XLSX.utils.book_new()

      // csv headers
      const dollarHeaders = this.dollarTypes.reduce((acc, item) => {
        acc.push(item.cash)

        return acc
      }, [])
      const columnHeader = ['#no', 'User', 'Register', 'Date', 'Time', 'Open/Close', ...dollarHeaders, 'total']

      const wbData = data.map((item, index) => {
        const tempData = item.cashFlows.reduce((acc, item) => {
          acc[item.cash_name] = item.cash_count
          return acc
        }, {})

        return {
          '#no': index + 1,
          User: item.prepareUser && item.prepareUser.username,
          Register: item.register && item.register.register_name,
          Date: dateMixins.methods.formatDate(item.cash_drop_date),
          Time: dateMixins.methods.covertDateTimeToTime(item.cash_drop_date),
          'Open/Close': item.float_type,
          ...tempData,
          total: item.final_total.toFixed(2)
        }
      })

      const ws = XLSX.utils.aoa_to_sheet([columnHeader], { origin: 'A3' })

      XLSX.utils.sheet_add_aoa(ws, [['Cash Float report']], { origin: 'A1' })

      XLSX.utils.sheet_add_aoa(ws, [['Dollars']], { origin: 'F2' })
      XLSX.utils.sheet_add_aoa(ws, [['Cents']], { origin: 'L2' })

      XLSX.utils.sheet_add_json(ws, wbData, { origin: 'A5', skipHeader: true })

      const mergedCells = [
        { s: { r: 1, c: 5 }, e: { r: 1, c: 10 } },
        { s: { r: 1, c: 11 }, e: { r: 1, c: 14 } }
      ]

      ws['!merges'] = mergedCells

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'Cash Float Report.xlsx')
    }
  },

  async beforeDestroy() {
    await this.set_registers_state({ action: 'registers', data: [] })
  }
}
</script>

<style></style>
